import { useEffect, useRef } from "react";
import type { PageHeadLinkTag, PageHeadMetaTag, PageHeadStructuredData, PageMetadata } from "~/app/api/pageData";

const HEAD_MARKER = "data-scha-route-head";
const HEAD_SELECTOR = `[${HEAD_MARKER}="true"]`;

interface HeadManagerProps {
  metadata: PageMetadata;
}

function appendMeta(tag: PageHeadMetaTag): HTMLMetaElement | null {
  let key: "http-equiv" | "property" | "name" | null = null;
  let value = "";

  if (tag.httpEquiv) {
    key = "http-equiv";
    value = tag.httpEquiv;
  } else if (tag.property) {
    key = "property";
    value = tag.property;
  } else if (tag.name) {
    key = "name";
    value = tag.name;
  }

  if (!key) {
    return null;
  }

  const node = document.createElement("meta");
  node.setAttribute(key, value);
  node.setAttribute("content", tag.content);
  return node;
}

function appendLink(tag: PageHeadLinkTag): HTMLLinkElement {
  const node = document.createElement("link");
  node.setAttribute("rel", tag.rel);
  node.setAttribute("href", tag.href);

  if (tag.hreflang) {
    node.setAttribute("hreflang", tag.hreflang);
  }
  if (tag.type) {
    node.setAttribute("type", tag.type);
  }
  if (tag.media) {
    node.setAttribute("media", tag.media);
  }
  if (tag.sizes) {
    node.setAttribute("sizes", tag.sizes);
  }
  if (tag.as) {
    node.setAttribute("as", tag.as);
  }
  if (tag.crossOrigin) {
    node.setAttribute("crossorigin", tag.crossOrigin);
  }

  return node;
}

function appendStructuredData(entry: PageHeadStructuredData): HTMLScriptElement {
  const node = document.createElement("script");
  node.setAttribute("type", "application/ld+json");
  node.textContent = JSON.stringify(entry.value);
  if (entry.id) {
    node.setAttribute("id", entry.id);
  }

  return node;
}

function clearOwnedHeadNodes() {
  document.head.querySelectorAll(HEAD_SELECTOR).forEach((node) => node.remove());
}

function appendOwnedNode(node: HTMLElement) {
  node.setAttribute(HEAD_MARKER, "true");
  document.head.appendChild(node);
}

export function HeadManager({ metadata }: HeadManagerProps) {
  const fallbackTitle = useRef(document.title);

  useEffect(() => {
    const nextTitle = metadata.title && metadata.title.length > 0 ? metadata.title : fallbackTitle.current;
    document.title = nextTitle;

    return () => {
      document.title = fallbackTitle.current;
    };
  }, [metadata.title]);

  useEffect(() => {
    clearOwnedHeadNodes();

    const head = metadata.head;
    if (head) {
      head.meta?.forEach((tag) => {
        const node = appendMeta(tag);
        if (node) {
          appendOwnedNode(node);
        }
      });
      head.links?.forEach((tag) => {
        appendOwnedNode(appendLink(tag));
      });
      head.structuredData?.forEach((entry) => {
        appendOwnedNode(appendStructuredData(entry));
      });
    }

    return () => {
      clearOwnedHeadNodes();
    };
  }, [metadata.head]);

  return null;
}
